import {
  Component,
  computed,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  signal,
  SimpleChanges
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";


import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";


import { firstValueFrom } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { CONSTANTS } from "../../../../../../../constants";
import { FeatureFlag, FeatureService } from "../../../../../../../core/feature-flags/feature.service";
import { JobIssue, Area, InspectionJob, Media, SyncableStatus, ImageType } from "../../../../../../../core/database/db";
import {
  DAILY_ITEM_BQ_STEP,
  formatBqStepOptionSubtitle,
  getBqStepDisplay,
  getBqStepLockToastDescription
} from "../../../../../../../domains/jobs/models/build-quality-step-display";
import { UNASSIGNED_AREA } from "../../../../../../../domains/areas/models/AreaDTO";
import { AreaService } from "../../../../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../../../../domains/jobIssues/services/job-issue-service";
import { JobService } from "../../../../../../../domains/jobs/services/job.service";
import { BqaImageItem } from "../../../../../../../domains/media/components/images-modal/bqa-image-item";
import { ImagesModalComponent } from "../../../../../../../domains/media/components/images-modal/images-modal.component";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../../../domains/media/utils/media-order.util";
import { FormFieldComponent } from "../../../../../../../shared/components/form-field/form-field.component";
import { PhotoCaptureComponent } from "../../../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../../../shared/components/section-header/section-header.component";
import { SelectFieldComponent } from "../../../../../../../shared/components/select-field/select-field.component";
import { StandardBottomSheetComponent } from "../../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { TextareaComponent } from "../../../../../../../shared/components/textarea/textarea.component";
import { UiToastService } from "../../../../../../../shared/components/toast/ui-toast.service";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { BlobService } from "../../../../../../../shared/services/blob.service";
import { LoggerService } from "../../../../../../../shared/services/logger.service";
import { VendorService } from "../../../../../../../domains/vendors/services/vendor-service";


interface Vendor {
  id: number;
  name: string;
}

export interface BqStepSelectOption {
  value: number;
  label: string;
  title: string;
  subtitle: string;
  disabled: boolean;
  buildQualityStep: number;
}

@Component({
  selector: "app-add-new-item-bottom-sheet",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    SelectFieldComponent,
    TextareaComponent,
    PhotoCaptureComponent,
    VerticalButtonGroupComponent
  ],
  templateUrl: "./add-new-item-bottom-sheet.component.html",
  styleUrl: "./add-new-item-bottom-sheet.component.scss"
})
export class AddNewItemBottomSheetComponent implements OnChanges, OnDestroy {
  @Input() lotId: number = 0;
  @Input() isOpen: boolean = false;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() itemAdded = new EventEmitter<JobIssue>();

  readonly maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  readonly maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  areas: Area[] = [];
  vendors: Vendor[] = [];
  jobs: InspectionJob[] = [];
  bqStepOptions: BqStepSelectOption[] = [];

  images = signal<Media[]>([]);
  allImagesRaw = computed(() => this.images().filter(im => !im.deleted));
  allImages = computed<SafeUrl[]>(() =>
    this.allImagesRaw()
      .map(img => {
        if (img.base64Image) {
          return this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image));
        }
        return img.resourceUri ?? "";
      })
      .filter(uri => !!uri)
      .map(uri => this.sanitizer.bypassSecurityTrustUrl(uri))
  );

  galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(image => (image.base64Image || image.resourceUri) && !image.deleted)
      .map(image => {
        let azureBlobUri = "";
        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(image.base64Image));
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }
        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      })
  );

  form = new FormGroup({
    lotAreaIdentifier: new FormControl<string | null>(null, Validators.required),
    text: new FormControl("", Validators.required),
    buildQualityStep: new FormControl<number | null>(null, Validators.required),
    externalVendorId: new FormControl<number | null>(null),
    photos: new FormControl<Media[]>([], [control => (control.value?.length ? null : { required: true })])
  });

  formSubmitted = false;
  private issueIdentifierForEncoding!: string;
  private objectUrls = new Set<string>();
  isSaving = signal(false);

  constructor(
    private jobService: JobService,
    private areaService: AreaService,
    private jobIssueService: JobIssueService,
    private vendorService: VendorService,
    private featureService: FeatureService,
    private toastService: UiToastService,
    private logger: LoggerService,
    public imageCompressionPipelineService: ImageCompressionPipelineService,
    private blobService: BlobService,
    private sanitizer: DomSanitizer,
    private dialog: Dialog
  ) { }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes["isOpen"]?.currentValue === true && changes["isOpen"]?.previousValue !== true) {
      this.resetForm();
      await this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  private async loadData(): Promise<void> {
    if (!this.lotId) {
      this.logger.warn("AddNewItemBottomSheet: loadData called without lotId", {
        lotId: this.lotId,
        isOpen: this.isOpen
      });
      this.toastService.error("Unable to load add item data. Please reopen and try again.");
      this.close();
      return;
    }

    try {
      const [rawAreas, rawVendors, jobs, bq234Enabled, bq6Enabled, bq9Enabled] = await Promise.all([
        this.areaService.getAreasByLotId(this.lotId),
        this.vendorService.getVendorsForLotId(this.lotId),
        this.jobService.getAllJobsForLot(this.lotId),
        firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ234)),
        firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ6)),
        firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ9))
      ]);

      const universalAreas = rawAreas.filter(area => !!area.buildQualityStepScope === false);
      this.areas = [...universalAreas, UNASSIGNED_AREA];
      this.vendors = this.vendorService.processVendorData(rawVendors)
        .map(v => ({ id: v.id, name: v.doingBusinessAs || v.name }));
      this.jobs = jobs;
      this.bqStepOptions = this.buildBqStepOptions(jobs, { bq234Enabled, bq6Enabled, bq9Enabled });
    } catch (error) {
      this.logger.error("AddNewItemBottomSheet: Failed to load data", {
        lotId: this.lotId,
        error
      });
      this.toastService.error("Unable to load add item data. Please try again.");
    }
  }

  private resetForm(): void {
    this.formSubmitted = false;
    this.images.set([]);
    this.bqStepOptions = [];
    this.form.reset({
      lotAreaIdentifier: null,
      text: "",
      buildQualityStep: DAILY_ITEM_BQ_STEP,
      externalVendorId: null,
      photos: []
    });
    this.issueIdentifierForEncoding = uuidv4();
  }

  private buildBqStepOptions(
    jobs: InspectionJob[],
    flags: { bq234Enabled: boolean; bq6Enabled: boolean; bq9Enabled: boolean }
  ): BqStepSelectOption[] {
    const options: BqStepSelectOption[] = [this.createBqStepOption(DAILY_ITEM_BQ_STEP, false)];

    for (const step of CONSTANTS.BUILD_QUALITY_STEPS.ALL) {
      if (!this.isInspectionStepVisible(step, flags)) {
        continue;
      }
      const job = jobs.find(j => j.buildQualityStep === step);
      options.push(this.createBqStepOption(step, !job?.isComplete));
    }

    return options;
  }

  private createBqStepOption(buildQualityStep: number, disabled: boolean): BqStepSelectOption {
    const title = getBqStepDisplay(buildQualityStep);
    const subtitle = formatBqStepOptionSubtitle(buildQualityStep);
    return {
      value: buildQualityStep,
      label: title,
      title,
      subtitle,
      disabled,
      buildQualityStep
    };
  }

  private isInspectionStepVisible(
    step: number,
    flags: { bq234Enabled: boolean; bq6Enabled: boolean; bq9Enabled: boolean }
  ): boolean {
    const bq = CONSTANTS.BUILD_QUALITY_STEPS;
    if (step === bq.BQ2 || step === bq.BQ3 || step === bq.BQ4) {
      return flags.bq234Enabled;
    }
    if (step === bq.BQ6) {
      return flags.bq6Enabled;
    }
    if (step === bq.BQ9) {
      return flags.bq9Enabled;
    }
    return true;
  }

  onLockedBqStepClick(option: unknown): void {
    const bqStep = (option as BqStepSelectOption).buildQualityStep;
    const description = getBqStepLockToastDescription(bqStep);
    this.toastService.info(`Unlocks after BQ${bqStep} (${description}) is marked complete.`);
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }

  get footerButtons(): VerticalButton[] {
    return [
      {
        label: this.isSaving() ? "Saving..." : "Add New Item",
        severity: "contrast",
        disabled: this.isSaving() || this.imageCompressionPipelineService.isCompressingImages(),
        loading: this.isSaving(),
        styleClass: "bqa-primary-btn",
        action: () => this.onSave(),
        testId: "add-new-item-btn-save"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.onCancel(),
        testId: "add-new-item-btn-cancel"
      }
    ];
  }

  onSheetOpenChange(isOpen: boolean): void {
    this.isOpen = isOpen;
    this.isOpenChange.emit(isOpen);
  }

  async onSave(): Promise<void> {
    this.formSubmitted = true;
    if (this.form.invalid) {
      return;
    }

    this.isSaving.set(true);

    const { text, buildQualityStep, externalVendorId, lotAreaIdentifier } = this.form.value;

    const job = this.jobs.find(j => j.buildQualityStep === buildQualityStep);
    if (!job) {
      this.logger.error("AddNewItemBottomSheet: No matching job for selected BQ step", {
        lotId: this.lotId,
        selectedStep: buildQualityStep,
        availableSteps: this.jobs.map(j => j.buildQualityStep)
      });
      this.toastService.error("Unable to add item because matching BQ job data is missing.");
      this.isSaving.set(false);
      return;
    }

    try {
      await this.imageCompressionPipelineService.waitForCompletion();

      const mediaToSave: Media[] = this.images().map(image => {
        const { file: _file, ...media } = image as MediaForCompression;
        return media;
      });

      const vendorName = externalVendorId
        ? this.vendors.find(v => v.id === externalVendorId)?.name
        : undefined;

      const savedLotAreaIdentifier = lotAreaIdentifier === UNASSIGNED_AREA.identifier
        ? undefined
        : (lotAreaIdentifier ?? undefined);

      const issue: Omit<JobIssue, "id"> = {
        identifier: this.issueIdentifierForEncoding,
        text: text!,
        lotAreaIdentifier: savedLotAreaIdentifier,
        isSelfAssigned: false,
        isComplete: false,
        isConfirmedByHomeowner: false,
        jobId: job.id,
        syncStatus: SyncableStatus.Pending,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const savedIssue = await this.jobIssueService.saveJobIssue(issue, mediaToSave, externalVendorId ?? null, vendorName);

      this.itemAdded.emit(savedIssue);
      this.isSaving.set(false);
      this.close();
    } catch (error) {
      this.logger.error("AddNewItemBottomSheet: Failed to save new item", {
        lotId: this.lotId,
        selectedStep: buildQualityStep,
        issueIdentifier: this.issueIdentifierForEncoding,
        error
      });
      this.isSaving.set(false);
      this.toastService.error("Unable to add item. Please try again.");
    }
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }

    const mediaRecords = await this.imageCompressionPipelineService.encode(
      files, this.issueIdentifierForEncoding, ImageType.Issue
    );
    this.images.update(existing => insertAt(existing, mediaRecords, insertIndex));
    this.form.get("photos")!.setValue(this.allImagesRaw(), { emitEvent: false });
    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      this.images.update(existing => existing.map(originalImage => {
        const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
        if (compressedImage) {
          const { file: _file, ...media } = compressedImage as MediaForCompression;
          return { ...originalImage, ...media } as Media;
        }
        return originalImage;
      }));
      this.form.get("photos")!.setValue(this.allImagesRaw(), { emitEvent: false });
    });
    return mediaRecords;
  }

  openImagesModal(index: number): void {
    this.dialog.open(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (mediaIdentifier: string) => this.handleImageDeleted(mediaIdentifier),
        onAnnotated: (mediaIdentifier: string, file: File) => this.handleImageAnnotated(mediaIdentifier, file)
      }
    });
  }

  private handleImageDeleted(mediaIdentifier: string): void {
    this.images.update(imgs => imgs.filter(img => img.fileIdentifier !== mediaIdentifier));
    const photosControl = this.form.get("photos")!;
    photosControl.setValue(this.allImagesRaw(), { emitEvent: false });
    photosControl.markAsTouched();
  }

  private async handleImageAnnotated(originalMediaIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.images().findIndex(img => img.fileIdentifier === originalMediaIdentifier);
    this.handleImageDeleted(originalMediaIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}
